<template>
  <div class="modernAudioPlayer" :class="{ disabled: !src || hasError }">
    <audio
      ref="audio"
      :src="src"
      preload="metadata"
      @loadedmetadata="handleMetadata"
      @durationchange="handleMetadata"
      @timeupdate="handleTimeUpdate"
      @play="isPlaying = true"
      @pause="isPlaying = false"
      @ended="handleEnded"
      @waiting="isLoading = true"
      @canplay="isLoading = false"
      @error="handleError"
    ></audio>

    <button
      type="button"
      class="playButton"
      :title="isPlaying ? 'Pausar' : 'Reproduzir'"
      :disabled="!src || hasError"
      @click="togglePlayback"
    >
      <span v-if="isLoading" class="playerSpinner"></span>
      <span v-else-if="isPlaying" class="pauseIcon"></span>
      <span v-else class="playIcon"></span>
    </button>

    <span class="timeLabel">{{ formatTime(currentTime) }}</span>
    <input
      class="progressRange"
      type="range"
      min="0"
      :max="effectiveDuration || 0"
      step="0.01"
      :value="currentTime"
      :disabled="!effectiveDuration || hasError"
      :style="{ '--audio-progress': `${progressPercentage}%` }"
      aria-label="Posição do áudio"
      @input="seek"
    />
    <span class="timeLabel duration">{{ formatTime(effectiveDuration) }}</span>
  </div>
</template>

<script>
export default {
  name: "AudioPlayer",
  props: {
    src: { type: String, default: "" },
    durationSeconds: { type: Number, default: 0 },
  },
  data() {
    return {
      currentTime: 0,
      mediaDuration: 0,
      isPlaying: false,
      isLoading: false,
      hasError: false,
    };
  },
  computed: {
    effectiveDuration() {
      const fallback = Number(this.durationSeconds) > 0
        ? Number(this.durationSeconds)
        : 0;
      const mediaIsPlausible =
        Number.isFinite(this.mediaDuration) &&
        this.mediaDuration > 0 &&
        (!fallback ||
          this.mediaDuration <= Math.max(fallback * 1.5, fallback + 2));
      if (mediaIsPlausible) {
        return this.mediaDuration;
      }
      return fallback;
    },
    progressPercentage() {
      if (!this.effectiveDuration) return 0;
      return Math.min((this.currentTime / this.effectiveDuration) * 100, 100);
    },
  },
  watch: {
    src() {
      this.currentTime = 0;
      this.mediaDuration = 0;
      this.isPlaying = false;
      this.hasError = false;
      this.$nextTick(() => this.$refs.audio?.load());
    },
  },
  beforeDestroy() {
    if (this.$refs.audio) this.$refs.audio.pause();
  },
  methods: {
    async togglePlayback() {
      const audio = this.$refs.audio;
      if (!audio || this.hasError) return;
      if (audio.paused) {
        this.isLoading = true;
        try {
          await audio.play();
          this.isLoading = false;
        } catch (_) {
          this.isLoading = false;
        }
      } else {
        audio.pause();
      }
    },
    handleMetadata() {
      const duration = Number(this.$refs.audio?.duration);
      // MediaRecorder WebM blobs can report Infinity. In this case, retain
      // the duration measured during recording as a reliable progress limit.
      this.mediaDuration =
        Number.isFinite(duration) && duration > 0 ? duration : 0;
      this.isLoading = false;
    },
    handleTimeUpdate() {
      const time = Number(this.$refs.audio?.currentTime);
      this.currentTime = Number.isFinite(time) ? time : 0;
    },
    handleEnded() {
      this.isPlaying = false;
      this.currentTime = 0;
      if (this.$refs.audio) this.$refs.audio.currentTime = 0;
    },
    handleError() {
      this.hasError = true;
      this.isLoading = false;
      this.isPlaying = false;
    },
    seek(event) {
      const value = Number(event.target.value);
      if (!this.$refs.audio || !Number.isFinite(value)) return;
      this.$refs.audio.currentTime = value;
      this.currentTime = value;
    },
    formatTime(value) {
      const total = Number.isFinite(Number(value))
        ? Math.max(Math.floor(Number(value)), 0)
        : 0;
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
        2,
        "0"
      )}`;
    },
  },
};
</script>

<style scoped>
.modernAudioPlayer {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 36px;
  gap: 7px;
  padding: 5px 8px;
  border: 1px solid #d7e3e0;
  border-radius: 7px;
  background: #f8fbfa;
}
.modernAudioPlayer audio {
  display: none;
}
.playButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 27px;
  width: 27px;
  height: 27px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: #006256;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}
.playButton:hover:not(:disabled) {
  background: #004f46;
  transform: scale(1.03);
}
.playButton:disabled {
  background: #aebeba;
  cursor: not-allowed;
}
.playIcon {
  width: 0;
  height: 0;
  margin-left: 2px;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 7px solid currentColor;
}
.pauseIcon {
  width: 7px;
  height: 9px;
  border-left: 2px solid;
  border-right: 2px solid;
}
.playerSpinner {
  width: 11px;
  height: 11px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: playerSpin 0.7s linear infinite;
}
.timeLabel {
  min-width: 31px;
  color: #526762;
  font-size: 10px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.timeLabel.duration {
  text-align: left;
}
.progressRange {
  --audio-progress: 0%;
  flex: 1;
  height: 4px;
  margin: 0;
  border-radius: 5px;
  outline: none;
  background: linear-gradient(
    to right,
    #006256 var(--audio-progress),
    #d4dfdc var(--audio-progress)
  );
  appearance: none;
  cursor: pointer;
}
.progressRange::-webkit-slider-thumb {
  width: 11px;
  height: 11px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #006256;
  box-shadow: 0 0 0 1px #006256;
  appearance: none;
}
.progressRange::-moz-range-thumb {
  width: 8px;
  height: 8px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #006256;
  box-shadow: 0 0 0 1px #006256;
}
.progressRange:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
@keyframes playerSpin {
  to {
    transform: rotate(360deg);
  }
}
</style>
